// import { navigate } from 'gatsby';  // TODO: this import will cause a build error. Possably because it exists in folder 'api' which is not allowed.
import { ApiTagTypes, baseApi, IPaginatedResponse } from './baseApi';
import {
    IResource,
    IResourceUpdates,
    IResourceVersion,
    IShareResourceViaNewGroupArgs,
    IShareResourceViaNewGroupResponse,
    IFailedInvite,
} from './resourceApi.types';
import { IGetAppResponse } from '@biolibtech/biolib-js/src/BioLib/resources/App';
import { IApp, OrganizationRole, IAccountAsAdmin, AccountRole } from '@biolibtech/biolib-js';
import { trimAndReplaceSpaces, isEmail } from '@components/common';
import config from '../config';
import { parseApiErrorMessage } from './utils';

export const resourceApi = baseApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (build) => ({
        getAppResource: build.query<IGetAppResponse, { uri: string }>({
            providesTags: [ApiTagTypes.app],
            query: (args) => ({
                url: '/app/',
                params: { uri: args.uri },
            }),
        }),
        getResource: build.query<IResource, { uri: string }>({
            query: (args) => ({
                url: '/resource/',
                params: { uri: args.uri },
            }),
        }),
        moveResource: build.mutation<IResource, { uuid: string; newUri: string; skipRedirect?: boolean }>({
            invalidatesTags: [ApiTagTypes.account, ApiTagTypes.experiments],
            query: (args) => ({
                url: `/resources/${args.uuid}/`,
                method: 'PATCH',
                body: { uri: args.newUri },
            }),
            transformResponse: (response: IResource, meta, args) => {
                if (!args.skipRedirect) {
                    window.location.assign(`/${response.uri}/settings/`);
                }
                return response;
            },
        }),
        updateAppResource: build.mutation<IApp, { uuid: string; updates: IResourceUpdates }>({
            invalidatesTags: [ApiTagTypes.app],
            query: (args) => ({
                url: `/apps/${args.uuid}/`,
                method: 'PATCH',
                body: args.updates,
            }),
        }),
        updateResource: build.mutation<IApp, { uuid: string; updates: IResourceUpdates }>({
            invalidatesTags: [ApiTagTypes.app],
            query: (args) => ({
                url: `/resources/${args.uuid}/`,
                method: 'PATCH',
                body: args.updates,
            }),
        }),
        deleteResource: build.mutation<IApp, { uuid: string }>({
            invalidatesTags: [ApiTagTypes.app],
            query: (args) => ({
                url: `/apps/${args.uuid}/`,
                method: 'DELETE',
            }),
        }),
        getResourceVersions: build.query<
            IPaginatedResponse<IResourceVersion>,
            { resourceUuid: string; page: number; pageSize: number }
        >({
            providesTags: [ApiTagTypes.resourceVersion],
            query: (args) => ({
                url: `/resources/${args.resourceUuid}/versions/`,
                params: { page: args?.page, page_size: args?.pageSize },
            }),
        }),
        shareResourceViaNewGroup: build.mutation<IShareResourceViaNewGroupResponse, IShareResourceViaNewGroupArgs>({
            invalidatesTags: [ApiTagTypes.account, ApiTagTypes.user, ApiTagTypes.experiments],
            queryFn: async (args, api, _extraOptions, baseQuery) => {
                const { resourceUuid, accountHandle, groupName, users, resourceName } = args;
                try {
                    const createAccountResult = await baseQuery({
                        url: '/accounts/',
                        method: 'POST',
                        body: {
                            account_handle: accountHandle,
                            display_name: groupName.trim(),
                        },
                    });

                    if (createAccountResult.error) {
                        return { error: createAccountResult.error };
                    }

                    const createdAccount = createAccountResult.data as IAccountAsAdmin;
                    const failedInvites: IFailedInvite[] = [];

                    for (const user of users) {
                        const inviteResult = await baseQuery({
                            url: `/account/${accountHandle}/members/invite/`,
                            method: 'POST',
                            body: { role: AccountRole.member, account_handle: user.account_handle },
                        });

                        if (inviteResult.error) {
                            failedInvites.push({
                                email: user.account_handle,
                                error: parseApiErrorMessage(inviteResult.error) || 'Failed to send invite',
                            });
                        }
                    }
                    const moveResult = await baseQuery({
                        url: `/resources/${resourceUuid}/`,
                        method: 'PATCH',
                        body: { uri: `${config.resourcePrefix}${accountHandle}/${resourceName}` },
                    });

                    if (moveResult.error) {
                        return { error: moveResult.error };
                    }

                    return { data: { account: createdAccount, failedInvites } };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR' as const,
                            error: error instanceof Error ? error.message : 'Unknown error occurred',
                        },
                    };
                }
            },
        }),
    }),
});

export const {
    useGetAppResourceQuery,
    useMoveResourceMutation,
    useUpdateAppResourceMutation,
    useUpdateResourceMutation,
    useDeleteResourceMutation,
    useGetResourceVersionsQuery,
    useShareResourceViaNewGroupMutation,
} = resourceApi;
